import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import DateInputContext from '../contexts/DateInputContext';
import DatePickerTriggerContext from '../contexts/DatePickerTriggerContext';
import { LAST_MONTH } from '../components/DateInput/constants';
import useRecentlyUsedRollingWindows from '../components/DateInput/RelativeInput/useRecentlyUsedRollingWindows';
import DateFieldsProvider from './DateFieldsProvider';
import DateFieldsContext from '../contexts/DateFieldsContext';

export interface DateInputProviderProps {
  children?: JSX.Element | JSX.Element[];
  initialDateRange?: DateRangeInput;
  initialRelativeDateRange?: RelativeDateRange;
  initialAdvancedRelativeDateRange?: AdvancedRelativeDateRange;
  initialDateField?: string;
  initialDataTypes?: string[];
}

const InitialDateFieldNameProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { dateFields } = useContext(DateFieldsContext);
  const { dateField, setDateFieldName } = useContext(DateInputContext);

  useEffect(() => {
    const selectedField = dateFields.find((f) => f.field === dateField);
    if (selectedField) {
      setDateFieldName(selectedField.name);
    } else {
      setDateFieldName(dateField);
    }
  }, [dateField, dateFields, setDateFieldName]);

  return <>{children}</>;
};

const useDatePickerTrigger = () => {
  const callbackRef = useRef<() => void>();
  const [isActive, setIsActiveState] = useState(false);
  const setIsActive = useCallback((active: boolean) => {
    setIsActiveState(active);
  }, []);
  const openDatePicker = useCallback(() => {
    setIsActiveState(true);
    if (callbackRef.current) callbackRef.current();
  }, []);
  const registerOpenCallback = useCallback((fn: () => void) => {
    callbackRef.current = fn;
  }, []);
  return { isActive, setIsActive, openDatePicker, registerOpenCallback };
};

const DateInputProvider = ({
  children,
  initialDateRange,
  initialDateField,
  initialRelativeDateRange,
  initialDataTypes,
  initialAdvancedRelativeDateRange,
}: DateInputProviderProps) => {
  const recentlyUsedRollingWindows = useRecentlyUsedRollingWindows();
  const datePickerTrigger = useDatePickerTrigger();
  const [dateRange, setDateRange] = useState<DateRangeInput | undefined>(
    initialDateRange,
  );
  const [relativeDateRange, setRelativeDateRange] = useState<
    RelativeDateRange | undefined
  >(() => {
    if (initialRelativeDateRange) {
      return initialRelativeDateRange;
    }

    if (!initialDateRange && !initialAdvancedRelativeDateRange) {
      return LAST_MONTH;
    }
  });
  const [advancedRelativeDateRange, setAdvancedRelativeDateRange] = useState<
    AdvancedRelativeDateRange | undefined
  >(initialAdvancedRelativeDateRange);
  const [dateField, setDateField] = useState<string>(
    initialDateField ? initialDateField : 'date',
  );
  const [dateFieldName, setDateFieldName] = useState<string>(
    initialDateField ? initialDateField : 'date',
  );
  const [dataTypes, setDataTypes] = useState<string[]>(
    initialDataTypes ? initialDataTypes : [],
  );
  const [isManuallyEntered, setIsManuallyEntered] = useState<boolean>(false);

  return (
    <DatePickerTriggerContext.Provider value={datePickerTrigger}>
      <DateInputContext.Provider
        value={{
          dateRange,
          setDateRange,
          relativeDateRange,
          setRelativeDateRange,
          advancedRelativeDateRange,
          setAdvancedRelativeDateRange,
          dateField,
          setDateField,
          dateFieldName,
          setDateFieldName,
          dataTypes,
          setDataTypes,
          recentlyUsedRollingWindows,
          isManuallyEntered,
          setIsManuallyEntered,
        }}
      >
        <DateFieldsProvider dataTypes={dataTypes}>
          <InitialDateFieldNameProvider>
            <>{children}</>
          </InitialDateFieldNameProvider>
        </DateFieldsProvider>
      </DateInputContext.Provider>
    </DatePickerTriggerContext.Provider>
  );
};

export default DateInputProvider;
